import { Menu, Moon, Sun, X } from 'lucide-react';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { navItems, resourceItems } from '../data/siteData';
import useDarkMode from '../hooks/useDarkMode';
import Button from './Button';
import Logo from './Logo';

function NavAnchor({ item, onClick }) {
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      className={({ isActive }) =>
        `focus-ring rounded-lg border-2 px-3 py-2 text-sm font-bold transition ${
          isActive ? 'border-ink bg-yellow text-ink shadow-hard-sm dark:border-ink dark:text-ink' : 'border-transparent text-ink hover:border-ink hover:bg-white dark:text-canvas dark:hover:border-canvas dark:hover:bg-white/10'
        }`
      }
    >
      {item.label}
    </NavLink>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { theme, toggleTheme } = useDarkMode();

  return (
    <header className="sticky top-0 z-50 border-b-2 border-ink bg-canvas dark:border-canvas dark:bg-night">
      <a href="#main-content" className="focus-ring sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-white focus:px-4 focus:py-2">
        Skip to content
      </a>
      <nav className="container-shell flex h-20 items-center justify-between" aria-label="Main navigation">
        <Logo />
        <div className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => <NavAnchor key={item.to} item={item} />)}
          <div className="group relative">
            <button className="focus-ring rounded-lg border-2 border-transparent px-3 py-2 text-sm font-bold text-ink hover:border-ink hover:bg-white dark:text-canvas dark:hover:border-canvas dark:hover:bg-white/10">
              Resources
            </button>
            <div className="invisible absolute right-0 top-12 w-48 translate-y-2 rounded-lg border-2 border-ink bg-white p-2 opacity-0 shadow-hard transition group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 dark:border-canvas dark:bg-[#252525] dark:shadow-[4px_4px_0_#FDF2E9]">
              {resourceItems.map((item) => <NavAnchor key={item.to} item={item} />)}
            </div>
          </div>
          <NavAnchor item={{ label: 'About', to: '/about' }} />
          <NavAnchor item={{ label: 'Contact', to: '/contact' }} />
        </div>
        <div className="hidden items-center gap-2 lg:flex">
          <button onClick={toggleTheme} className="focus-ring grid h-11 w-11 place-items-center rounded-lg border-2 border-ink bg-white text-ink shadow-hard-sm active:translate-x-0.5 active:translate-y-0.5 active:shadow-none dark:border-canvas dark:bg-[#252525] dark:text-canvas dark:shadow-[2px_2px_0_#FDF2E9]" aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <Button to="/demo">Book Demo</Button>
        </div>
        <button onClick={() => setOpen(true)} className="focus-ring grid h-11 w-11 place-items-center rounded-lg border-2 border-ink bg-primary text-ink shadow-hard-sm lg:hidden dark:border-ink" aria-label="Open menu">
          <Menu size={22} />
        </button>
      </nav>
      {open && (
        <div className="fixed inset-0 z-50 bg-ink/60 lg:hidden" role="dialog" aria-modal="true">
          <div className="ml-auto min-h-screen w-full max-w-sm border-l-2 border-ink bg-canvas p-5 shadow-hard-lg dark:border-canvas dark:bg-night">
            <div className="flex items-center justify-between">
              <Logo />
              <button onClick={() => setOpen(false)} className="focus-ring grid h-11 w-11 place-items-center rounded-lg border-2 border-ink bg-white shadow-hard-sm dark:border-canvas dark:bg-[#252525]" aria-label="Close menu">
                <X size={22} />
              </button>
            </div>
            <div className="mt-8 grid gap-2">
              {[...navItems, ...resourceItems, { label: 'About', to: '/about' }, { label: 'Contact', to: '/contact' }].map((item) => (
                <NavAnchor key={item.to} item={item} onClick={() => setOpen(false)} />
              ))}
              <button onClick={toggleTheme} className="focus-ring mt-4 flex items-center gap-3 rounded-lg border-2 border-ink bg-white px-4 py-3 font-bold shadow-hard-sm dark:border-canvas dark:bg-[#252525]">
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />} Toggle theme
              </button>
              <Button to="/demo" onClick={() => setOpen(false)} className="mt-3">Book Demo</Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
